import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  BookOpen, Plus, Pencil, Trash2, ArrowUpRight, ArrowDownRight, 
  RefreshCw, Search, Eye, X, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function GLCodes() {
  const [loading, setLoading] = useState(true);
  const [glCodes, setGlCodes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    code: '',
    description: '',
    is_income: false,
    category: ''
  });
  
  // Transaction viewer state
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedGLCode, setSelectedGLCode] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Transaction editing state
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showEditTxnDialog, setShowEditTxnDialog] = useState(false);
  const [txnForm, setTxnForm] = useState({
    date: '',
    description: '',
    reference: '',
    amount: '',
    gl_code: '',
    vat_type: 'no_vat'
  });
  const [savingTxn, setSavingTxn] = useState(false);

  useEffect(() => {
    fetchGLCodes();
  }, []);

  const fetchGLCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/gl-codes`);
      setGlCodes(response.data.gl_codes || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load GL codes'));
    } finally {
      setLoading(false);
    }
  };

  const viewTransactions = async (glCode) => {
    setSelectedGLCode(glCode);
    setShowTransactions(true);
    setLoadingTransactions(true);
    
    try {
      const response = await axios.get(`${API_URL}/finance/gl-codes/${encodeURIComponent(glCode.code)}/transactions`);
      setTransactions(response.data.transactions || []);
      setTransactionSummary(response.data.summary || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load transactions'));
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const openEditTxnDialog = (txn) => {
    setEditingTransaction(txn);
    setTxnForm({
      date: txn.date || '',
      description: txn.description || '',
      reference: txn.reference || '',
      amount: txn.amount?.toString() || '',
      gl_code: txn.gl_code || selectedGLCode?.code || '',
      vat_type: txn.vat_type || 'no_vat'
    });
    setShowEditTxnDialog(true);
  };

  const saveTransaction = async () => {
    if (!editingTransaction) return;
    setSavingTxn(true);
    
    const collection = editingTransaction.is_income ? 'income' : 'expenses';
    
    try {
      await axios.put(`${API_URL}/finance/transactions/${collection}/${editingTransaction.id}`, {
        date: txnForm.date || null,
        description: txnForm.description || null,
        reference: txnForm.reference || null,
        amount: txnForm.amount ? parseFloat(txnForm.amount) : null,
        gl_code: txnForm.gl_code || null,
        vat_type: txnForm.vat_type || null
      });
      
      toast.success('Transaction updated');
      setShowEditTxnDialog(false);
      
      // Refresh transactions
      if (selectedGLCode) {
        viewTransactions(selectedGLCode);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update transaction'));
    } finally {
      setSavingTxn(false);
    }
  };

  const deleteTransaction = async (txn) => {
    if (!confirm(`Delete this transaction?\n${txn.description}\nR${txn.amount?.toLocaleString()}`)) return;
    
    const collection = txn.is_income ? 'income' : 'expenses';
    
    try {
      await axios.delete(`${API_URL}/finance/transactions/${collection}/${txn.id}`);
      toast.success('Transaction deleted');
      
      // Refresh transactions
      if (selectedGLCode) {
        viewTransactions(selectedGLCode);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete transaction'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingCode) {
        await axios.put(`${API_URL}/finance/gl-codes/${encodeURIComponent(editingCode.code)}`, form);
        toast.success('GL code updated');
      } else {
        await axios.post(`${API_URL}/finance/gl-codes`, form);
        toast.success('GL code created');
      }

      setShowDialog(false);
      resetForm();
      fetchGLCodes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save GL code'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (glCode) => {
    if (!confirm(`Delete GL code "${glCode.code}"? This will not affect existing transactions.`)) return;

    try {
      await axios.delete(`${API_URL}/finance/gl-codes/${encodeURIComponent(glCode.code)}`);
      toast.success('GL code deleted');
      fetchGLCodes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete GL code'));
    }
  };

  const openEditDialog = (glCode) => {
    setEditingCode(glCode);
    setForm({
      code: glCode.code,
      description: glCode.description || '',
      is_income: glCode.is_income || false,
      category: glCode.category || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingCode(null);
    setForm({
      code: '',
      description: '',
      is_income: false,
      category: ''
    });
  };

  const filteredCodes = glCodes.filter(code => {
    const matchesSearch = 
      code.code?.toLowerCase().includes(search.toLowerCase()) ||
      code.description?.toLowerCase().includes(search.toLowerCase());
    
    if (filterType === 'income') return matchesSearch && code.is_income;
    if (filterType === 'expense') return matchesSearch && !code.is_income;
    return matchesSearch;
  });

  const incomeCount = glCodes.filter(c => c.is_income).length;
  const expenseCount = glCodes.filter(c => !c.is_income).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="gl-codes-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">GL Codes</h1>
            <p className="text-muted-foreground mt-1">Manage General Ledger codes for transaction categorization</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-gl-code-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add GL Code
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Total GL Codes</p>
                  <p className="text-2xl font-bold">{glCodes.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ArrowUpRight className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Income Codes</p>
                  <p className="text-2xl font-bold text-green-700">{incomeCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ArrowDownRight className="h-8 w-8 text-red-600" />
                <div>
                  <p className="text-sm text-red-700">Expense Codes</p>
                  <p className="text-2xl font-bold text-red-700">{expenseCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search GL codes..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-gl-codes"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48" data-testid="filter-gl-type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="income">Income Only</SelectItem>
                  <SelectItem value="expense">Expense Only</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchGLCodes} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* GL Codes Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              GL Codes ({filteredCodes.length})
            </CardTitle>
            <CardDescription>
              General Ledger codes used for categorizing bank transactions
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
                Loading GL codes...
              </div>
            ) : filteredCodes.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No GL codes found</h3>
                <p className="text-muted-foreground mb-4">
                  {search ? 'Try adjusting your search' : 'Add a GL code or import bank data to learn codes automatically'}
                </p>
                <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add GL Code
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((glCode) => (
                    <TableRow key={glCode.code}>
                      <TableCell className="font-medium">{glCode.code}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {glCode.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={glCode.is_income 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : 'bg-red-50 text-red-700 border-red-200'
                          }
                        >
                          {glCode.is_income ? (
                            <><ArrowUpRight className="h-3 w-3 mr-1" /> Income</>
                          ) : (
                            <><ArrowDownRight className="h-3 w-3 mr-1" /> Expense</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {glCode.category ? (
                          <Badge variant="secondary">{glCode.category}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => viewTransactions(glCode)} 
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`view-gl-${glCode.code}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(glCode)} data-testid={`edit-gl-${glCode.code}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(glCode)} 
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-gl-${glCode.code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {editingCode ? 'Edit GL Code' : 'Add GL Code'}
              </DialogTitle>
              <DialogDescription>
                {editingCode 
                  ? 'Update the GL code details. Changing the code name will update all linked transactions.' 
                  : 'Create a new General Ledger code for categorizing transactions'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="code">GL Code Name *</Label>
                  <Input
                    id="code"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g., FUEL, Salaries-Direct"
                    required
                    data-testid="gl-code-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional description"
                    data-testid="gl-description-input"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_income" className="text-sm font-medium">
                      Income Code
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Toggle on if this code represents income (money in)
                    </p>
                  </div>
                  <Switch
                    id="is_income"
                    checked={form.is_income}
                    onCheckedChange={(checked) => setForm({ ...form, is_income: checked })}
                    data-testid="gl-income-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Mapped Category</Label>
                  <Input
                    id="category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g., fuel, salary, other"
                    data-testid="gl-category-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maps to expense/income categories for reporting
                  </p>
                </div>

                <div className="p-3 border rounded-lg">
                  <p className="text-sm font-medium mb-2">Preview:</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {form.code || 'GL_CODE'}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge 
                      className={form.is_income 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                      }
                    >
                      {form.is_income ? 'Income' : 'Expense'}
                    </Badge>
                    {form.category && (
                      <>
                        <span className="text-muted-foreground">→</span>
                        <Badge variant="secondary">{form.category}</Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="save-gl-code-btn">
                  {formLoading ? 'Saving...' : (editingCode ? 'Update' : 'Create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Transactions Viewer Modal */}
        <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Transactions for: {selectedGLCode?.code}
              </DialogTitle>
              <DialogDescription>
                {transactionSummary && (
                  <div className="flex flex-wrap gap-4 mt-2">
                    <Badge variant="outline">{transactionSummary.total_transactions} transactions</Badge>
                    <Badge className="bg-blue-100 text-blue-700">Total: {formatCurrency(transactionSummary.total_amount)}</Badge>
                    {transactionSummary.income_count > 0 && (
                      <Badge className="bg-green-100 text-green-700">{transactionSummary.income_count} income</Badge>
                    )}
                    {transactionSummary.expense_count > 0 && (
                      <Badge className="bg-red-100 text-red-700">{transactionSummary.expense_count} expenses</Badge>
                    )}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {loadingTransactions ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                <p>No transactions found for this GL code</p>
              </div>
            ) : (
              <ScrollArea className="h-[50vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn, idx) => (
                      <TableRow key={txn.id || idx} className={txn.is_income ? 'bg-green-50/50' : ''}>
                        <TableCell className="whitespace-nowrap">{txn.date}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={txn.description}>
                          {txn.description}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate text-muted-foreground" title={txn.reference}>
                          {txn.reference || '-'}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${txn.is_income ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(txn.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={txn.is_income ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                          >
                            {txn.is_income ? 'Income' : 'Expense'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditTxnDialog(txn)} className="h-7 w-7 p-0">
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteTransaction(txn)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransactions(false)}>
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Transaction Dialog */}
        <Dialog open={showEditTxnDialog} onOpenChange={setShowEditTxnDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Transaction</DialogTitle>
              <DialogDescription>
                Update the transaction details below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="txn-date">Date</Label>
                  <Input
                    id="txn-date"
                    type="date"
                    value={txnForm.date}
                    onChange={(e) => setTxnForm({...txnForm, date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="txn-amount">Amount (R)</Label>
                  <Input
                    id="txn-amount"
                    type="number"
                    step="0.01"
                    value={txnForm.amount}
                    onChange={(e) => setTxnForm({...txnForm, amount: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="txn-description">Description</Label>
                <Input
                  id="txn-description"
                  value={txnForm.description}
                  onChange={(e) => setTxnForm({...txnForm, description: e.target.value})}
                  placeholder="Transaction description"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="txn-reference">Reference</Label>
                <Input
                  id="txn-reference"
                  value={txnForm.reference}
                  onChange={(e) => setTxnForm({...txnForm, reference: e.target.value})}
                  placeholder="Reference number"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="txn-gl-code">GL Code</Label>
                  <select
                    id="txn-gl-code"
                    value={txnForm.gl_code}
                    onChange={(e) => setTxnForm({...txnForm, gl_code: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select GL Code</option>
                    {glCodes.map(gl => (
                      <option key={gl.code} value={gl.code}>{gl.code}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="txn-vat-type">VAT Type</Label>
                  <select
                    id="txn-vat-type"
                    value={txnForm.vat_type}
                    onChange={(e) => setTxnForm({...txnForm, vat_type: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="no_vat">No VAT</option>
                    <option value="inclusive">VAT Inclusive (15%)</option>
                    <option value="exclusive">VAT Exclusive (15%)</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditTxnDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveTransaction} disabled={savingTxn}>
                {savingTxn ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
